"use client";

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { AttributedLink } from "@/components/marketing/attributed-link";
import {
  trackHeaderCtaClicked,
  type HeaderCtaLocation,
} from "@/lib/analytics/cta-events";

/**
 * Where the site-header "Get started" CTA sends visitors: the value-first free
 * funnel (start a real job, see the documents, then claim) rather than
 * /pricing, which had the weakest engagement on the site. Shared by the
 * desktop header and the mobile menu so they cannot drift apart.
 */
export const HEADER_GET_STARTED_HREF = "/start-project";

interface HeaderCtaLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  ctaLocation: HeaderCtaLocation;
  children: ReactNode;
}

/**
 * The tracked site-header "Get started" link. Keeps ad attribution on the
 * href (AttributedLink) and fires a consent-respecting `header_cta_clicked`.
 */
export function HeaderCtaLink({
  ctaLocation,
  children,
  onClick,
  ...props
}: HeaderCtaLinkProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    trackHeaderCtaClicked({
      ctaLocation,
      destination: HEADER_GET_STARTED_HREF,
      ctaLabel: typeof children === "string" ? children : "Get started",
    });
    onClick?.(event);
  }

  return (
    <AttributedLink
      href={HEADER_GET_STARTED_HREF}
      onClick={handleClick}
      {...props}
    >
      {children}
    </AttributedLink>
  );
}
